/**
 * 会话 store（zustand + persist，localStorage）。
 * 对齐 ops-console `src/store/auth.ts` 语义（调研§2），key 改为 web 专属。
 * 持久化 token + 过期 + 身份，供 axios 拦截器（非 React 上下文）经 getState() 读取。
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/lib/queryClient';

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  /** epoch ms：accessToken 失效时刻。 */
  accessTokenExpiresAt: number;
  userId: string;
  /** 后端 identityState（身份 4 态）。 */
  identityState: string | null;
}

interface AuthState extends Partial<AuthSession> {
  setSession: (s: AuthSession) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const AUTH_STORAGE_KEY = 'workability-web-auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: undefined,
      refreshToken: undefined,
      accessTokenExpiresAt: undefined,
      userId: undefined,
      identityState: undefined,
      setSession: (s) =>
        set({
          accessToken: s.accessToken,
          refreshToken: s.refreshToken,
          accessTokenExpiresAt: s.accessTokenExpiresAt,
          userId: s.userId,
          identityState: s.identityState ?? undefined,
        }),
      clear: () => {
        // 登出/401 清会话时,一并清 react-query 缓存,避免换账号后残留上一个账号的
        // 订单/收益/收款账户等数据(跨账号串数据)。
        queryClient.clear();
        set({
          accessToken: undefined,
          refreshToken: undefined,
          accessTokenExpiresAt: undefined,
          userId: undefined,
          identityState: undefined,
        });
      },
      isAuthenticated: () => {
        const { accessToken, accessTokenExpiresAt } = get();
        if (!accessToken) return false;
        if (accessTokenExpiresAt && Date.now() >= accessTokenExpiresAt) return false;
        return true;
      },
    }),
    { name: AUTH_STORAGE_KEY },
  ),
);
